import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";
import { loadHighLevelEmbed } from "@/lib/highlevel";

const PERKS = [
  "Free strategy call — no strings attached",
  "Plain-language audit of your website",
  "An honest assessment of whether we're a fit",
];

export default function Contact() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    loadHighLevelEmbed();
    // When the HighLevel form is submitted, send them to the calendar to book a time.
    const onMessage = (e) => {
      if (!e.origin || !e.origin.includes("leadconnectorhq.com")) return;
      const payload = JSON.stringify(e.data || "").toLowerCase();
      if (
        payload.includes("submit") ||
        payload.includes("success") ||
        payload.includes("thank")
      ) {
        navigate("/free-strategy-call");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBook = (e) => {
    if (e) e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 450);
    } else {
      scrollToId("book");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden border-t border-slate-200 bg-ink py-24 md:py-32"
    >
      <div className="pointer-events-none absolute -right-40 top-0 h-[480px] w-[480px] rounded-full bg-flame/10 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <FadeUp>
              <Eyebrow>Start</Eyebrow>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
                Your next customer is{" "}
                <span className="text-flame">searching.</span>
              </h2>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-mist md:text-xl">
                Tell us where you are and where you need to be. Todd reads
                every message himself — no forms disappearing into a void.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div
                data-testid="contact-owner-line"
                className="mt-8 flex items-center gap-4"
              >
                <img
                  src="/img/todd-portrait.webp"
                  alt="Todd Meisler"
                  data-testid="contact-avatar"
                  className="h-16 w-16 shrink-0 rounded-full border-2 border-white object-cover shadow-md"
                />
                <p className="text-lg font-semibold text-slate-900">
                  Todd answers every message{" "}
                  <span className="text-flame">personally</span>
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.15}>
              <ul className="mt-10 space-y-4">
                {PERKS.map((p) => (
                  <li
                    key={p}
                    className="flex items-center gap-3 text-lg text-slate-800"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-flame" />
                    {p}
                  </li>
                ))}
              </ul>
              <button
                data-testid="contact-book-instead-button"
                onClick={goBook}
                className="mt-8 rounded-2xl border border-slate-300 px-6 py-3 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-all duration-300 hover:border-flame/60 hover:text-flame"
              >
                Prefer to pick a time? Book a call
              </button>
            </FadeUp>
          </div>

          <FadeUp delay={0.1}>
            <div
              data-testid="contact-highlevel-form"
              className="glass overflow-hidden rounded-2xl border border-slate-200 p-2 md:p-3"
            >
              <iframe
                src="https://api.leadconnectorhq.com/widget/form/7MtQReSvAQJwzeVzROwe"
                title="Contact Us Form"
                id="inline-7MtQReSvAQJwzeVzROwe"
                data-layout={'{"id":"INLINE"}'}
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Contact Us Form"
                data-height="450"
                data-layout-iframe-id="inline-7MtQReSvAQJwzeVzROwe"
                data-form-id="7MtQReSvAQJwzeVzROwe"
                data-cookie-consent="true"
                data-cookie-consent-provider="auto"
                data-testid="contact-highlevel-iframe"
                style={{ width: "100%", height: 470, minHeight: 450, border: "none", borderRadius: 8 }}
              />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
